'use client';

import * as React from 'react';
import {
  Room,
  RoomEvent,
  ConnectionState,
  ConnectionQuality,
  VideoQuality,
  Track,
  ParticipantEvent,
} from 'livekit-client';
import type { LocalTrackPublication, Participant } from 'livekit-client';
import type { RemoteTrackPublication } from 'livekit-client';
import {
  getEffectiveVideoQualityProfile,
  getVideoCaptureMediaConstraints,
  readVideoQualityProfile,
} from '@/lib/video-quality-profile';
import { readEconomyModeEnabled } from '@/lib/economyMode';
import {
  getEffectiveScreenShareQualityProfile,
  getScreenShareMediaConstraints,
  readScreenShareQualityProfile,
} from '@/lib/screen-share-profile';
import { isForceRelaySessionActive } from '@/lib/ice-path-policy';
import {
  getScreenShareAdaptModeForConnection,
  getScreenShareFpsCapForConnection,
  pinIncomingScreenShareQuality,
} from '@/lib/screen-share-quality-guard';
import {
  SCREEN_SHARE_COEXIST_CAMERA_MAX_FRAMERATE,
  capLocalCameraForPoorNetwork,
  capLocalCameraForScreenShare,
  restoreLocalCameraPublishBitrate,
} from '@/lib/camera-publish-bitrate';
import {
  FALSE_POOR_RECOVERY_INTERVAL_MS,
  incomingVideoQualityForConnection,
  shouldAttemptFalsePoorRecovery,
  shouldHardCapPublishBitrate,
} from '@/lib/network-mitigation-policy';

const SCREEN_SHARE_CAPTURE_FPS_GOOD = 15;
const SCREEN_SHARE_CAPTURE_FPS_POOR = 8;

/**
 * Janela de startup do screenshare (ms).
 *
 * Durante esse período após publicar o track de screenshare, NENHUMA operação de adaptação é
 * executada — nem applyConstraints na câmera, nem mudança de simulcast layer, nem redução de FPS.
 *
 * Motivo: quando um novo track WebRTC é publicado, o browser e o SFU iniciam uma renegociação
 * ICE. Qualquer applyConstraints ou setVideoQuality disparado durante essa renegociação adiciona
 * mensagens de controle simultâneas e pode colapsar o estimador de banda do SFU, causando o
 * estado "Poor" que vemos na UI logo após o início do screenshare. Não é problema de rede — é
 * interferência do nosso próprio código.
 *
 * 5 s é suficiente para o ICE completar (tipicamente 1-3 s) com margem confortável.
 */
export const SCREENSHARE_STARTUP_GRACE_MS = 5_000;

/**
 * Após reconnect ICE, o estimador de banda do SFU ainda está instável.
 * Aplicar mitigação (Excellent / HIGH) imediatamente costumava colapsar o BWE
 * (ex.: 100 Mbps → 5 kbps) quando câmera + tela republicavam juntos.
 */
export const RECONNECT_GRACE_MS = 8_000;

/**
 * Connect via TURN (forceRelay) estabiliza o path, mas o score inicial do LiveKit
 * costuma oscilar Poor/Lost nos primeiros segundos — se mitigarmos cedo, a câmera
 * fica em 15 fps e a camada alta do simulcast não sobe.
 */
export const TURN_CONNECT_GRACE_MS = 20_000;

export function isWithinScreenShareStartupGrace(
  screenShareStartedAt: number | null,
  now: number = Date.now(),
): boolean {
  return (
    screenShareStartedAt !== null && now - screenShareStartedAt < SCREENSHARE_STARTUP_GRACE_MS
  );
}

export function isWithinReconnectGrace(
  reconnectedAt: number | null,
  now: number = Date.now(),
  graceMs: number = RECONNECT_GRACE_MS,
): boolean {
  return reconnectedAt !== null && now - reconnectedAt < graceMs;
}

export function reconnectGraceMsForSession(): number {
  return isForceRelaySessionActive() ? TURN_CONNECT_GRACE_MS : RECONNECT_GRACE_MS;
}

function hasLocalScreenShareVideo(room: Room): boolean {
  const pub = room.localParticipant.getTrackPublication(Track.Source.ScreenShare);
  return !!(pub?.track && pub.kind === Track.Kind.Video);
}

function getLocalVideoMediaStreamTrack(
  room: Room,
  source: Track.Source,
): MediaStreamTrack | undefined {
  const pub = room.localParticipant.getTrackPublication(source);
  const t = pub?.track as { mediaStreamTrack?: MediaStreamTrack } | undefined;
  const ms = t?.mediaStreamTrack;
  return ms && ms.readyState === 'live' ? ms : undefined;
}

/**
 * Ajusta só o que ESTE cliente assina (visão local). Não altera o que os outros
 * recebem uns dos outros — cada subscriber tem seu próprio setVideoQuality.
 * Preferimos quase não usar isso em Poor (ver network-mitigation-policy).
 */
function setIncomingCameraMaxQuality(room: Room, maxQuality: VideoQuality) {
  room.remoteParticipants.forEach((participant) => {
    participant.videoTrackPublications.forEach((publication) => {
      if (!publication.isSubscribed || publication.kind !== Track.Kind.Video) {
        return;
      }
      if (publication.source !== Track.Source.Camera) {
        return;
      }
      const remote = publication as RemoteTrackPublication;
      try {
        remote.setVideoQuality(maxQuality);
      } catch {
        // Non-simulcast publications may not support quality switching.
      }
    });
  });
}

/**
 * While screen sharing, the camera becomes a thumbnail: capture ≤ 640×360 @ 15 fps and
 * publish capped (~300 kbps top layer, lower layers proportional) so the uplink is spent
 * on the screen — the content everyone is actually looking at. All simulcast layers stay
 * active (SFU score unaffected). Restored when screen share ends.
 *
 * Antes: `max 960×540` — no perfil medium (já 540p) era no-op e a câmera continuava
 * publicando 850 kbps + camadas ao lado da tela (~1 Mbps) → uplink 2,5–3 Mbps e travas.
 *
 * Called only AFTER the screenshare startup grace period to avoid interfering
 * with ICE renegotiation.
 */
export async function tightenCameraCaptureForScreenShare(room: Room) {
  const msTrack = getLocalVideoMediaStreamTrack(room, Track.Source.Camera);
  if (msTrack) {
    try {
      await msTrack.applyConstraints({
        width: { max: 640 },
        height: { max: 360 },
        frameRate: { max: SCREEN_SHARE_COEXIST_CAMERA_MAX_FRAMERATE },
        aspectRatio: { ideal: 16 / 9 },
      });
    } catch {
      // applyConstraints may be unsupported or denied on some platforms/browsers.
    }
  }
  await capLocalCameraForScreenShare(room);
}

/**
 * Restaura o teto de publish da câmera — ou, se a tela ainda estiver compartilhada,
 * mantém o cap de coexistência (senão Good/Excellent desfaria o cap durante a apresentação).
 */
async function restoreOrKeepCameraPublishCap(room: Room): Promise<void> {
  if (hasLocalScreenShareVideo(room)) {
    await capLocalCameraForScreenShare(room);
    return;
  }
  await restoreLocalCameraPublishBitrate(room);
}

async function restoreCameraCaptureAfterScreenShare(room: Room) {
  const msTrack = getLocalVideoMediaStreamTrack(room, Track.Source.Camera);
  if (msTrack) {
    try {
      const economy = readEconomyModeEnabled();
      const effective = getEffectiveVideoQualityProfile(readVideoQualityProfile(), economy);
      await msTrack.applyConstraints(getVideoCaptureMediaConstraints(effective));
    } catch {
      // applyConstraints may be unsupported or denied on some platforms/browsers.
    }
  }
  // Devolve o teto de publish mesmo se o score ainda estiver Poor neste instante.
  await restoreLocalCameraPublishBitrate(room);
}

/**
 * Congestão real (Lost): baixa captura + teto de publish.
 * Poor sozinho NÃO capa bitrate — isso prendia o encoder baixo e o score
 * LiveKit ficava "Ruim" com 0% perda (espiral 180p @ ~140 kbps).
 */
export async function adaptLocalCameraForNetwork(
  room: Room,
  quality: ConnectionQuality,
): Promise<void> {
  const pub = room.localParticipant.getTrackPublication(Track.Source.Camera);
  if (!pub?.track) return;
  const msTrack = (pub.track as { mediaStreamTrack?: MediaStreamTrack }).mediaStreamTrack;
  if (!msTrack || msTrack.readyState !== 'live') return;
  const sharing = hasLocalScreenShareVideo(room);
  try {
    if (quality === ConnectionQuality.Lost) {
      // Lost real: alivia SÓ o upload deste cliente (não a sala). Mantém 540p max
      // para não destruir a camada HIGH do simulcast se o link voltar.
      await msTrack.applyConstraints({
        width: { max: 960 },
        height: { max: 540 },
        frameRate: { max: 15 },
      });
      if (shouldHardCapPublishBitrate(quality)) {
        await capLocalCameraForPoorNetwork(room);
      }
      return;
    }
    if (quality === ConnectionQuality.Poor) {
      // Só FPS — Poor falso (TURN/BWE) não deve encolher resolução de publish.
      await msTrack.applyConstraints({
        frameRate: { max: 20 },
      });
      return;
    }
    if (quality === ConnectionQuality.Good || quality === ConnectionQuality.Excellent) {
      if (sharing) {
        // Tela ativa: câmera fica em modo thumbnail (não volta ao perfil cheio).
        await tightenCameraCaptureForScreenShare(room);
        return;
      }
      const economy = readEconomyModeEnabled();
      const effective = getEffectiveVideoQualityProfile(readVideoQualityProfile(), economy);
      const base = getVideoCaptureMediaConstraints(effective);
      const profileMaxFps =
        typeof base.frameRate === 'object' && base.frameRate && 'max' in base.frameRate
          ? Number(base.frameRate.max) || 30
          : 30;
      await msTrack.applyConstraints({
        ...base,
        frameRate: { max: profileMaxFps },
      });
      await restoreLocalCameraPublishBitrate(room);
    }
  } catch {
    // applyConstraints may be unsupported or denied on some platforms/browsers.
  }
}

/**
 * Throttle screen-capture under congestion (após grace ICE).
 * Poor: só FPS (12) — Poor falso após ICE não pode encolher a tela para 540p.
 * Lost: modo poor (540p@8). Economia explícita: modo economy.
 */
export async function adaptLocalScreenShareForNetwork(
  room: Room,
  quality: ConnectionQuality,
): Promise<void> {
  if (!hasLocalScreenShareVideo(room)) return;
  const msTrack = getLocalVideoMediaStreamTrack(room, Track.Source.ScreenShare);
  if (!msTrack) return;

  const economy = readEconomyModeEnabled();
  const profile = getEffectiveScreenShareQualityProfile(
    readScreenShareQualityProfile(),
    economy,
  );
  const mode = getScreenShareAdaptModeForConnection(quality, economy);
  const fpsCap = getScreenShareFpsCapForConnection(quality);

  try {
    if (quality === ConnectionQuality.Poor || quality === ConnectionQuality.Lost) {
      const constraints = getScreenShareMediaConstraints(profile, mode);
      if (fpsCap != null) {
        constraints.frameRate = { max: fpsCap };
      }
      await msTrack.applyConstraints(constraints);
      return;
    }
    if (quality === ConnectionQuality.Good || quality === ConnectionQuality.Excellent) {
      await msTrack.applyConstraints({
        ...getScreenShareMediaConstraints(profile, mode),
        ...(mode === 'default' ? { frameRate: { max: SCREEN_SHARE_CAPTURE_FPS_GOOD } } : {}),
      });
    }
  } catch {
    // Fallback: só FPS se constraints de resolução falharem.
    try {
      if (quality === ConnectionQuality.Lost) {
        await msTrack.applyConstraints({ frameRate: { max: SCREEN_SHARE_CAPTURE_FPS_POOR } });
      } else if (quality === ConnectionQuality.Poor) {
        await msTrack.applyConstraints({
          frameRate: { max: fpsCap ?? SCREEN_SHARE_CAPTURE_FPS_POOR },
        });
      } else if (quality === ConnectionQuality.Good || quality === ConnectionQuality.Excellent) {
        await msTrack.applyConstraints({ frameRate: { max: SCREEN_SHARE_CAPTURE_FPS_GOOD } });
      }
    } catch {
      // ignore
    }
  }
}

function applyNetworkMitigation(room: Room, quality: ConnectionQuality) {
  const incoming = incomingVideoQualityForConnection(quality);
  if (incoming !== null) {
    setIncomingCameraMaxQuality(room, incoming);
  }
  pinIncomingScreenShareQuality(room);
  void adaptLocalCameraForNetwork(room, quality);
  void adaptLocalScreenShareForNetwork(room, quality);
}

/**
 * Em path TURN estável, preferir fps sobre resolução quando o encoder precisar degradar.
 * Sem isso o Chromium costuma cair para 5–10 fps em vez de baixar um pouco a resolução.
 *
 * NÃO eleva `networkPriority` da câmera para 'high': isso a colocava no mesmo nível do
 * áudio no pacer do Chrome e, em uplink saturado (câmera + tela), o áudio picotava.
 * Áudio deve continuar com prioridade acima de qualquer vídeo.
 */
export async function preferFramerateOnCameraPublish(room: Room): Promise<void> {
  if (!isForceRelaySessionActive()) return;
  const pub = room.localParticipant.getTrackPublication(Track.Source.Camera);
  const sender = pub?.track?.sender;
  if (!sender || typeof sender.getParameters !== 'function') return;
  try {
    const params = sender.getParameters();
    const next = {
      ...params,
      degradationPreference: 'maintain-framerate' as RTCDegradationPreference,
    };
    await sender.setParameters(next);
  } catch {
    // Nem todo browser/track aceita setParameters pós-publish.
  }
}

export function useSubscriberNetworkAdaptation(room: Room) {
  const debounceRef = React.useRef<ReturnType<typeof setTimeout>>();
  // Timestamp em que o track de screenshare foi publicado. Null quando não há screenshare ativo.
  const screenShareStartedAtRef = React.useRef<number | null>(null);
  const reconnectedAtRef = React.useRef<number | null>(null);
  const activeGraceMsRef = React.useRef(RECONNECT_GRACE_MS);
  const poorSinceRef = React.useRef<number | null>(null);
  const lastFalsePoorRecoveryRef = React.useRef<number | null>(null);
  // Timer para aplicar tightenCamera APÓS o grace period.
  const cameraTightenTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectGraceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const falsePoorRecoveryTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    const clearDebounce = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = undefined;
      }
    };

    const clearFalsePoorRecoveryTimer = () => {
      if (falsePoorRecoveryTimerRef.current) {
        clearTimeout(falsePoorRecoveryTimerRef.current);
        falsePoorRecoveryTimerRef.current = null;
      }
    };

    const attemptFalsePoorRecovery = () => {
      const now = Date.now();
      const quality = room.localParticipant.connectionQuality;
      if (
        !shouldAttemptFalsePoorRecovery(
          quality,
          poorSinceRef.current,
          now,
          lastFalsePoorRecoveryRef.current,
        )
      ) {
        return;
      }
      lastFalsePoorRecoveryRef.current = now;
      // Libera camada alta + teto de encode para o BWE/adaptiveStream subirem.
      setIncomingCameraMaxQuality(room, VideoQuality.HIGH);
      void restoreOrKeepCameraPublishCap(room);
      console.info(
        '[network-adapt] recovery de Poor suspeito (0% loss / BWE) — liberando HIGH temporariamente',
      );
    };

    const scheduleFalsePoorRecovery = (quality: ConnectionQuality) => {
      clearFalsePoorRecoveryTimer();
      if (quality !== ConnectionQuality.Poor) {
        poorSinceRef.current = null;
        return;
      }
      if (poorSinceRef.current === null) {
        poorSinceRef.current = Date.now();
      }
      falsePoorRecoveryTimerRef.current = setTimeout(() => {
        falsePoorRecoveryTimerRef.current = null;
        attemptFalsePoorRecovery();
        // Reagenda enquanto continuar Poor.
        if (room.localParticipant.connectionQuality === ConnectionQuality.Poor) {
          scheduleFalsePoorRecovery(ConnectionQuality.Poor);
        }
      }, FALSE_POOR_RECOVERY_INTERVAL_MS);
    };

    const beginStabilityGrace = (graceMs: number) => {
      clearDebounce();
      activeGraceMsRef.current = graceMs;
      reconnectedAtRef.current = Date.now();
      if (reconnectGraceTimerRef.current) {
        clearTimeout(reconnectGraceTimerRef.current);
      }
      reconnectGraceTimerRef.current = setTimeout(() => {
        reconnectGraceTimerRef.current = null;
        reconnectedAtRef.current = null;
        const q = room.localParticipant.connectionQuality;
        applyNetworkMitigation(room, q);
        scheduleFalsePoorRecovery(q);
      }, graceMs);
    };

    const scheduleApply = (quality: ConnectionQuality) => {
      clearDebounce();
      debounceRef.current = setTimeout(() => {
        debounceRef.current = undefined;

        // Durante o grace period do screenshare, ignoramos qualquer evento de qualidade.
        // A qualidade pode cair temporariamente enquanto o ICE renegocia o novo track — não é
        // um problema real de rede, e agir sobre ela piora a situação.
        if (isWithinScreenShareStartupGrace(screenShareStartedAtRef.current)) {
          return;
        }
        if (
          isWithinReconnectGrace(
            reconnectedAtRef.current,
            Date.now(),
            activeGraceMsRef.current,
          )
        ) {
          return;
        }

        applyNetworkMitigation(room, quality);
        scheduleFalsePoorRecovery(quality);
      }, 400);
    };

    const onQuality = (quality: ConnectionQuality, participant: Participant) => {
      if (!participant.isLocal) {
        return;
      }
      scheduleApply(quality);
    };

    const onConnected = () => {
      if (!isForceRelaySessionActive()) return;
      // TURN: ignora Poor transitório enquanto o BWE sobe as camadas do simulcast.
      beginStabilityGrace(TURN_CONNECT_GRACE_MS);
      void preferFramerateOnCameraPublish(room);
    };

    const onReconnected = () => {
      // Só reativa vídeo; NÃO força Excellent/HIGH — isso colapsava o BWE do SFU.
      void room.startVideo().catch(() => {});
      beginStabilityGrace(reconnectGraceMsForSession());
      void preferFramerateOnCameraPublish(room);
    };

    const onLocalTrackPublished = (publication: LocalTrackPublication) => {
      if (publication.source !== Track.Source.Camera || publication.kind !== Track.Kind.Video) {
        return;
      }
      void preferFramerateOnCameraPublish(room);
    };

    const onTrackSubscribed = (
      _track: unknown,
      publication: RemoteTrackPublication,
    ) => {
      if (publication.kind !== Track.Kind.Video) {
        return;
      }
      if (publication.source !== Track.Source.ScreenShare) {
        return;
      }
      try {
        publication.setVideoQuality(VideoQuality.HIGH);
      } catch {
        // ignore
      }
    };

    room.on(RoomEvent.ConnectionQualityChanged, onQuality);
    room.on(RoomEvent.Connected, onConnected);
    room.on(RoomEvent.Reconnected, onReconnected);
    room.on(RoomEvent.TrackSubscribed, onTrackSubscribed);
    room.localParticipant.on(ParticipantEvent.LocalTrackPublished, onLocalTrackPublished);

    // Se o hook montar depois do Connected (race comum), ainda aplica o grace TURN.
    if (room.state === ConnectionState.Connected && isForceRelaySessionActive()) {
      beginStabilityGrace(TURN_CONNECT_GRACE_MS);
      void preferFramerateOnCameraPublish(room);
    }

    return () => {
      clearDebounce();
      clearFalsePoorRecoveryTimer();
      if (reconnectGraceTimerRef.current) {
        clearTimeout(reconnectGraceTimerRef.current);
      }
      room.off(RoomEvent.ConnectionQualityChanged, onQuality);
      room.off(RoomEvent.Connected, onConnected);
      room.off(RoomEvent.Reconnected, onReconnected);
      room.off(RoomEvent.TrackSubscribed, onTrackSubscribed);
      room.localParticipant.off(ParticipantEvent.LocalTrackPublished, onLocalTrackPublished);
    };
  }, [room]);

  /** Screen + camera coexistence: lower capture resolution for camera while screen is published. */
  React.useEffect(() => {
    const lp = room.localParticipant;

    const onPublished = (publication: LocalTrackPublication) => {
      if (
        publication.source !== Track.Source.ScreenShare ||
        publication.kind !== Track.Kind.Video
      ) {
        return;
      }

      // Marca o início do screenshare para ativar o grace period.
      screenShareStartedAtRef.current = Date.now();

      // Cancela qualquer timer pendente de tighten anterior.
      if (cameraTightenTimerRef.current) {
        clearTimeout(cameraTightenTimerRef.current);
      }

      // Agenda o tighten da câmera APÓS o grace period, quando a renegociação ICE já terminou.
      // Fazer isso imediatamente causava applyConstraints simultâneos com o ICE, derrubando a
      // qualidade da conexão logo após o início do screenshare.
      cameraTightenTimerRef.current = setTimeout(() => {
        cameraTightenTimerRef.current = null;
        if (!hasLocalScreenShareVideo(room)) return; // screenshare foi encerrado durante a espera
        void tightenCameraCaptureForScreenShare(room);
        // Com o ICE estabilizado, aplica a mitigação de qualidade com o valor real.
        applyNetworkMitigation(room, lp.connectionQuality);
      }, SCREENSHARE_STARTUP_GRACE_MS);
    };

    const onUnpublished = (publication: LocalTrackPublication) => {
      if (publication.source !== Track.Source.ScreenShare) {
        return;
      }
      if (hasLocalScreenShareVideo(room)) {
        return;
      }

      // Cancela o tighten caso o screenshare tenha sido encerrado antes do grace period.
      if (cameraTightenTimerRef.current) {
        clearTimeout(cameraTightenTimerRef.current);
        cameraTightenTimerRef.current = null;
      }
      screenShareStartedAtRef.current = null;

      void restoreCameraCaptureAfterScreenShare(room);
      applyNetworkMitigation(room, lp.connectionQuality);
    };

    lp.on(ParticipantEvent.LocalTrackPublished, onPublished);
    lp.on(ParticipantEvent.LocalTrackUnpublished, onUnpublished);

    return () => {
      if (cameraTightenTimerRef.current) {
        clearTimeout(cameraTightenTimerRef.current);
      }
      lp.off(ParticipantEvent.LocalTrackPublished, onPublished);
      lp.off(ParticipantEvent.LocalTrackUnpublished, onUnpublished);
    };
  }, [room]);
}
